import { buildTitle } from "@/lib/seo-quality";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Phone, ArrowRight, ArrowLeft, CheckCircle2, AlertCircle, MapPin, Snowflake, Wrench } from "lucide-react";
import { PageHeader } from "./fordon";
import { clampDescription, robotsMeta } from "@/lib/seo-quality";
import { assessModellSerie } from "@/lib/seo-families/modell-serier";
import { getModellSerie, MODELL_SERIER, type ModellSerie } from "@/data/modell-serier";
import { SerieStock } from "@/components/SerieStock";

export const Route = createFileRoute("/modeller/$brand/$serie")({
  codeSplitGroupings: [["loader", "component"]],
  loader: ({ params }) => {
    const serie = getModellSerie(params.brand, params.serie);
    if (!serie) throw notFound();
    return { serie, robots: robotsMeta(assessModellSerie(serie)) };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const m = loaderData.serie;
    const url = `https://www.akhusbilfritid.se/modeller/${m.brandSlug}/${m.slug}`;
    const desc = clampDescription(m.metaDesc);
    return {
      meta: [
        ...loaderData.robots,
        { title: buildTitle(m.metaTitle) },
        { name: "description", content: desc },
        {
          name: "keywords",
          content: `${m.keyword}, ${m.name.toLowerCase()} begagnad, ${m.name.toLowerCase()} pris, ${m.brandName} ${m.category}`,
        },
        { property: "og:type", content: "website" },
        { property: "og:title", content: buildTitle(m.metaTitle) },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: m.name,
            brand: { "@type": "Brand", name: m.brandName },
            category: m.category === "husbil" ? "Husbil" : "Husvagn",
            description: m.intro,
            offers: {
              "@type": "AggregateOffer",
              priceCurrency: "SEK",
              availability: "https://schema.org/InStock",
              seller: { "@type": "AutoDealer", name: "AK Husbil & Fritid" },
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: m.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
              { "@type": "ListItem", position: 2, name: "Modeller", item: "https://www.akhusbilfritid.se/modeller" },
              { "@type": "ListItem", position: 3, name: m.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: SeriePage,
  notFoundComponent: () => (
    <div className="container-edge py-20 text-center">
      <h1 className="font-display text-3xl">Modellserien finns inte</h1>
      <Link to="/modeller" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-accent">
        <ArrowLeft size={14} /> Till alla modellserier
      </Link>
    </div>
  ),
});

function SeriePage() {
  const { serie: m } = Route.useLoaderData() as { serie: ModellSerie };
  const others = MODELL_SERIER.filter((x) => x.slug !== m.slug || x.brandSlug !== m.brandSlug).slice(0, 3);

  return (
    <>
      <PageHeader
        label={`${m.brandName} · ${m.category === "husbil" ? "Husbil" : "Husvagn"}`}
        title={<>{m.h1}</>}
        sub={m.intro}
      />

      <section className="container-edge -mt-6 pb-4">
        <div className="flex flex-wrap gap-3">
          <Link
            to={m.category === "husbil" ? "/husbilar/$brand" : "/husvagnar/$brand"}
            params={{ brand: m.brandSlug }}
            className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
          >
            Se {m.brandName} i lager <ArrowRight size={14} />
          </Link>
          <a
            href="tel:0708629743"
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary"
          >
            <Phone size={16} /> 070-862 97 43
          </a>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary"
          >
            <MapPin size={16} /> Boka besök i Kalix
          </Link>
        </div>
      </section>

      <section className="container-edge py-14 md:py-20">
        <div className="grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <p className="eyebrow">Planlösningar</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl">
              Så ser <span className="font-italic-display text-accent">{m.name}</span> ut inuti.
            </h2>
            <ul className="mt-6 space-y-3">
              {m.layouts.map((l) => (
                <li key={l} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 shrink-0 text-accent" size={20} />
                  <span className="text-foreground/85">{l}</span>
                </li>
              ))}
            </ul>

            <p className="eyebrow mt-12">Passar dig som</p>
            <ul className="mt-4 space-y-3">
              {m.perfectFor.map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 shrink-0 text-accent" size={20} />
                  <span className="text-foreground/85">{t}</span>
                </li>
              ))}
            </ul>

            <div className="mt-12 grid gap-6 md:grid-cols-2">
              <div className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-xl">Styrkor</h3>
                <ul className="mt-4 space-y-2 text-sm text-foreground/85">
                  {m.strengths.map((s) => (
                    <li key={s} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 shrink-0 text-accent" size={16} />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-xl">Att tänka på</h3>
                <ul className="mt-4 space-y-2 text-sm text-foreground/85">
                  {m.weaknesses.map((s) => (
                    <li key={s} className="flex items-start gap-2">
                      <AlertCircle className="mt-0.5 shrink-0 text-accent" size={16} />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-3xl border border-border bg-card p-6 md:p-8">
              <h3 className="font-display text-2xl">Prisbild</h3>
              <dl className="mt-5 divide-y divide-border text-sm">
                <div className="py-3">
                  <dt className="text-muted-foreground">Nypris</dt>
                  <dd className="mt-1 font-medium">{m.priceNew}</dd>
                </div>
                <div className="py-3">
                  <dt className="text-muted-foreground">Begagnat prisspann</dt>
                  <dd className="mt-1 font-medium leading-relaxed">{m.priceUsed}</dd>
                </div>
              </dl>
              <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
                Spannen är våra observationer på svensk begagnatmarknad och varierar med skick,
                utrustning och säsong. Vi lämnar alltid pris på det enskilda exemplaret.
              </p>
              <Link to="/prisindex" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-accent">
                Se AK Prisindex <ArrowRight size={14} />
              </Link>
            </div>

            <div className="rounded-3xl border border-border bg-warm p-6 md:p-8">
              <div className="flex items-center gap-2">
                <Snowflake className="text-accent" size={18} />
                <h3 className="font-display text-xl">Vinter i Norrbotten</h3>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-foreground/85">{m.winter}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-warm">
        <div className="container-edge py-14 md:py-20">
          <div className="grid gap-10 md:grid-cols-2">
            <div>
              <div className="flex items-center gap-2">
                <Wrench className="text-accent" size={18} />
                <p className="eyebrow">Fel vi återkommande åtgärdar</p>
              </div>
              <ul className="mt-5 space-y-2 text-sm text-foreground/85">
                {m.vanligaFel.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <AlertCircle className="mt-0.5 shrink-0 text-accent" size={16} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="eyebrow">Vår kontroll före köp</p>
              <ul className="mt-5 space-y-2 text-sm text-foreground/85">
                {m.checklist.map((c) => (
                  <li key={c} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 shrink-0 text-accent" size={16} />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="container-edge py-14 md:py-20">
        <p className="eyebrow">Vår erfarenhet</p>
        <h2 className="mt-3 font-display text-3xl md:text-4xl">
          Det här ser vi på <span className="font-italic-display text-accent">{m.name}</span>.
        </h2>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {m.djup.map((p) => (
            <p key={p.slice(0, 30)} className="text-base leading-relaxed text-foreground/85">
              {p}
            </p>
          ))}
        </div>
      </section>

      <section className="container-edge pb-14">
        <p className="eyebrow">Vanliga frågor</p>
        <h2 className="mt-3 font-display text-3xl md:text-4xl">
          Om <span className="font-italic-display text-accent">{m.name}</span>.
        </h2>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {m.faq.map((f) => (
            <div key={f.q} className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-display text-xl">{f.q}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <SerieStock
        brandName={m.brandName}
        serieName={m.name}
        brandSlug={m.brandSlug}
        category={m.category}
      />

      <section className="container-edge pb-20">
        <p className="eyebrow">Fler modellserier</p>
        <div className="mt-6 grid gap-6 md:grid-cols-3">
          {others.map((o) => (
            <Link
              key={`${o.brandSlug}/${o.slug}`}
              to="/modeller/$brand/$serie"
              params={{ brand: o.brandSlug, serie: o.slug }}
              className="group rounded-3xl border border-border bg-card p-6 transition hover:-translate-y-1 hover:shadow-xl"
            >
              <p className="eyebrow">{o.brandName}</p>
              <h3 className="mt-3 font-display text-2xl">{o.name}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{o.intro.slice(0, 110)}…</p>
              <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-accent">
                Läs mer <ArrowRight size={14} className="transition group-hover:translate-x-0.5" />
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-8">
          <Link to="/modeller" className="inline-flex items-center gap-1 text-sm font-semibold text-accent">
            <ArrowLeft size={14} /> Alla modellserier
          </Link>
        </div>
      </section>
    </>
  );
}
